import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import { prop } from 'ramda'
import { apiStateSelectors } from '../../../api/apiState'
import { reportingApiEvents, reportingApiSelectors } from '../../../api/reporting'
import { DateRangePicker, DimmerLoader, Header, Modal, Table, Select, Switch } from '../../../../views/components'
import { format, startOfMonth, endOfMonth, subMonths, set } from 'date-fns'
import { getCurrentTaxSeason } from 'utils/date'
import ReportingIcon from '../../../../views/components/atoms/Icons/Descriptive/ReportsFill'
import DownloadIcon from '../../../../views/components/atoms/Icons/Doc/DocumentDownload'
import PdfIcon from '../../../../views/components/atoms/Icons/Doc/PdfAlt'
import styles from './ReportingTable.module.scss'
import { $TSFixMe } from 'types/ts-migrate'
import { useGTMDispatch } from '@elgorditosalsero/react-gtm-hook'
import { userApiSelectors } from 'modules/api/user'
import { invoiceApiEvents } from 'modules/api/invoice'
// Utility: Generic change handler compatible with react-select's MultiValue
import type { MultiValue, ActionMeta } from 'react-select'
import { leaseStatusesForSelect, leaseTypesForSelect } from 'modules/api/reporting/utils'

const ReportingTable = () => {
  const dispatch = useDispatch()
  const dateFormat = 'yyyy-MM-dd'
  const sendDataToGTM = useGTMDispatch()

  const [selectedReport, setSelectedReport] = useState<$TSFixMe>(null)
  const [selectedOwners, setSelectedOwners] = useState<$TSFixMe>([])
  const [selectedInvoiceTypes, setSelectedInvoiceTypes] = useState<$TSFixMe>([])
  const [selectedAgents, setSelectedAgents] = useState<$TSFixMe>([])
  const [isIncludeDraftsSelected, setIsIncludeDraftsSelected] = useState<$TSFixMe>(false)
  const [selectedLeaseTypes, setSelectedLeaseTypes] = useState<$TSFixMe>([])
  const [selectedLeaseStatuses, setSelectedLeaseStatuses] = useState<$TSFixMe>([])

  const defaultDates = {
    start: format(startOfMonth(Date.now()), dateFormat),
    end: format(Date.now(), dateFormat),
  }

  const { start: taxStart, end: taxEnd } = getCurrentTaxSeason()

  useEffectOnce(() => {
    dispatch(invoiceApiEvents.invoiceTypes_request())
    dispatch(reportingApiEvents.properties_request())
    dispatch(reportingApiEvents.quickSearch_request({ tags: ['Agent'] }))
    dispatch(reportingApiEvents.quickSearch_request({ tags: ['Owner'] }))
  })

  const downloadReport = useCallback(
    action => {
      dispatch(action)
    },
    [dispatch],
  )

  const isOwnerRole = useSelector(userApiSelectors.isOwnerRole)
  const isValidSupportStaff = useSelector(userApiSelectors.isValidSupportStaff)

  const reports = [
    {
      id: 'accountsReceivable',
      name: 'Accounts Receivable',
      dateParams: null,
      invoiceTypeParams: {
        invoiceTypes: selectedInvoiceTypes,
      },
      agentParams: {
        agents: selectedAgents,
      },
      action: reportingApiEvents.accountsReceivable_request,
      actionArgs: { dataType: 'excel' },
      isVisible: true,
    },
    {
      id: 'agencyCommissionReport',
      name: 'Agency Commission Report',
      dateParams: defaultDates,
      action: reportingApiEvents.agencyCommission_request,
      actionArgs: { dataType: 'excel' },
      isVisible: isOwnerRole || isValidSupportStaff,
    },
    {
      id: 'agentCommissionReport',
      name: 'Agent Commission Report',
      dateParams: defaultDates,
      action: reportingApiEvents.agentCommission_request,
      actionArgs: { dataType: 'excel' },
      isVisible: isOwnerRole || isValidSupportStaff,
    },
    {
      id: 'rentRoll',
      name: 'Rent Roll',
      dateParams: defaultDates,
      agentParams: {
        agents: selectedAgents,
      },
      action: reportingApiEvents.rentRoll_request,
      actionArgs: { dataType: 'excel' },
      isVisible: true,
    },
    {
      id: 'cashFlow',
      name: 'Cash Flow',
      dateParams: defaultDates,
      // action: reportingApiEvents.cashFlow_request({ dataType: 'excel', startDate, endDate }),
      action: reportingApiEvents.cashFlow_request,
      actionArgs: { dataType: 'excel' },
      isVisible: isValidSupportStaff,
    },
    {
      id: 'deposits',
      name: 'Deposit Report',
      dateParams: defaultDates,
      agentParams: {
        agents: selectedAgents,
      },
      action: reportingApiEvents.deposits_request,
      actionArgs: { dataType: 'excel' },
      isVisible: true,
    },
    {
      id: 'tenancySchedule',
      name: 'Tenancy Schedule',
      dateParams: null,
      agentParams: {
        agents: selectedAgents,
      },
      leaseStatusParams: {
        leaseStatuses: selectedLeaseStatuses,
      },
      leaseTypeParams: {
        leaseTypes: selectedLeaseTypes,
      },
      action: reportingApiEvents.tenancySchedule_request,
      actionArgs: { dataType: 'excel', shouldIncludeDrafts: !isIncludeDraftsSelected },
      isVisible: true,
    },
    {
      id: 'cashDepositStatement',
      name: 'Cash Deposit Statement',
      dateParams: {
        start: format(startOfMonth(subMonths(Date.now(), 3)), dateFormat),
        end: format(endOfMonth(Date.now()), dateFormat),
      },
      action: reportingApiEvents.cashDepositStatement_request,
      actionArgs: { dataType: 'text/csv' },
      pdfActionArgs: { dataType: 'application/pdf' },
      isVisible: true,
    },
    {
      id: 'propertyTaxStatement',
      name: 'Property Tax Statement',
      dateParams: {
        start: format(taxStart, dateFormat),
        end: format(taxEnd, dateFormat),
      },
      ownerParams: {
        owners: selectedOwners,
      },
      action: reportingApiEvents.propertyTaxStatement_request,
      pdfActionArgs: { dataType: 'application/pdf' },
      isVisible: true,
    },
    {
      id: 'ownerStatement',
      name: 'Owner Statement',
      dateParams: {
        start: format(taxStart, dateFormat),
        end: format(taxEnd, dateFormat),
      },
      ownerParams: {
        owners: selectedOwners,
      },
      action: reportingApiEvents.ownerStatement_request,
      pdfActionArgs: { dataType: 'application/pdf' },
      isVisible: true,
    },
    {
      id: 'availableFunds',
      name: 'Available Funds',
      dateParams: null,
      action: reportingApiEvents.availableFunds_request,
      actionArgs: { dataType: 'excel' },
      isVisible: true,
    },
    {
      id: 'ficaCompliance',
      name: 'FICA Compliance',
      dateParams: null,
      action: reportingApiEvents.ficaCompliance_request,
      actionArgs: { dataType: 'excel' },
      isVisible: true,
    },
  ]
    .filter(item => !!item?.isVisible)
    .sort((a, b) => {
      const aIsStatement = a.name.includes('Statement')
      const bIsStatement = b.name.includes('Statement')
      if (aIsStatement !== bIsStatement) return aIsStatement ? 1 : -1
      return a.name.localeCompare(b.name)
    })

  const loadingEvents = reports.map(report => report.action)
  const isLoading = useSelector(state => apiStateSelectors.isLoading(state)(loadingEvents))

  const ownersForSelect = useSelector(state => reportingApiSelectors.getOwnersForSelect(state))
  const agentsForSelect = useSelector(state => reportingApiSelectors.getAgentsForSelect(state))
  const invoiceTypesForSelect = useSelector(state => reportingApiSelectors.getInvoiceTypesForSelect(state))

  useEffect(() => {
    if (!isLoading) {
      setSelectedReport(null)
      setSelectedOwners([])
      setSelectedInvoiceTypes([])
      setSelectedAgents([])
      setSelectedLeaseStatuses([])
      setSelectedLeaseTypes([])
    }
  }, [isLoading])

  // Utility: Generate "Select All" option
  const getSelectAllOption = (selected: $TSFixMe[], allOptions: $TSFixMe[]) => {
    const areAllSelected = selected.length === allOptions.length
    return {
      label: areAllSelected ? 'Deselect All' : 'Select All',
      value: 'select-all',
    }
  }

  type SelectOption = { label: string; value: string }

  const createMultiSelectChangeHandler = (
    selectedItems: $TSFixMe[],
    allOptions: $TSFixMe[],
    setSelectedItems: (items: $TSFixMe[]) => void,
  ) => {
    const selectAllOptionValue = 'select-all'
    const areAllSelected = selectedItems.length === allOptions.length

    return (newValue: MultiValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => {
      // Convert readonly MultiValue to mutable array
      const selected = Array.isArray(newValue) ? [...newValue] : []
      const isTogglingAll = selected?.some((option: any) => option.value === selectAllOptionValue)

      if (isTogglingAll && !areAllSelected) {
        const flatOptions = allOptions.filter(opt => !('options' in opt))
        setSelectedItems(flatOptions)
      } else if (isTogglingAll && areAllSelected) {
        setSelectedItems([])
      } else {
        setSelectedItems(selected)
      }
    }
  }

  // Options
  const selectAllInvoiceTypesOption = getSelectAllOption(selectedInvoiceTypes, invoiceTypesForSelect)

  // Handlers
  const handleOwnersChange = createMultiSelectChangeHandler(selectedOwners, ownersForSelect, setSelectedOwners)

  const handleAgentsChange = createMultiSelectChangeHandler(selectedAgents, agentsForSelect, setSelectedAgents)

  const handleInvoiceTypesChange = createMultiSelectChangeHandler(
    selectedInvoiceTypes,
    invoiceTypesForSelect,
    setSelectedInvoiceTypes,
  )

  return (
    <div className={styles.root}>
      <Header icon={<ReportingIcon />} text="Reporting" />
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Cell head>Report</Table.Cell>
            <Table.Cell head>&nbsp;</Table.Cell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {reports.map((report, i) => (
            <Table.Row key={`report-row-${i}`}>
              <Table.Cell>{report.name}</Table.Cell>
              {report.pdfActionArgs ? (
                <Table.Cell className={styles['action-cell']}>
                  <button
                    type="button"
                    onClick={() => setSelectedReport({ ...report, actionArgs: report.pdfActionArgs })}
                  >
                    <PdfIcon width={28} />
                  </button>
                </Table.Cell>
              ) : null}
              {report.id !== 'propertyTaxStatement' && report.id !== 'ownerStatement' && (
                <Table.Cell className={styles['action-cell']}>
                  <button type="button" onClick={() => setSelectedReport(report)}>
                    <DownloadIcon />
                  </button>
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Modal isOpen={selectedReport?.id} padding="sm" showClose={true} onClose={() => setSelectedReport(null)}>
        <Modal.Header title={selectedReport?.name} />
        <Modal.Body
          actions={
            isLoading
              ? []
              : selectedReport?.id === 'accountsReceivable'
              ? [
                  {
                    label: 'Download',
                    onClick: () => {
                      downloadReport(
                        selectedReport?.action({
                          filters: {
                            agents: selectedAgents,
                            owners: [],
                            invoiceTypes: selectedInvoiceTypes,
                          },
                        }),
                      ),
                        sendDataToGTM({
                          event: 'download_report',
                          report_name: selectedReport?.name,
                        })
                    },
                  },
                ]
              : selectedReport?.id === 'tenancySchedule'
              ? [
                  {
                    label: 'Download',
                    onClick: () => {
                      downloadReport(
                        selectedReport?.action({
                          filters: {
                            agents: selectedAgents,
                            leaseStatuses: selectedLeaseStatuses,
                            leaseTypes: selectedLeaseTypes,
                          },
                        }),
                      ),
                        sendDataToGTM({
                          event: 'download_report',
                          report_name: selectedReport?.name,
                        })
                    },
                  },
                ]
              : selectedReport?.id === 'rentRoll' || selectedReport?.id === 'deposits'
              ? [
                  {
                    label: 'Download',
                    onClick: () => {
                      downloadReport(
                        selectedReport?.action({
                          timeframe: {
                            start: selectedReport.dateParams.start,
                            finish: selectedReport.dateParams.end,
                          },
                          filters: {
                            agents: selectedAgents,
                          },
                        }),
                      ),
                        sendDataToGTM({
                          event: 'download_report',
                          report_name: selectedReport?.name,
                        })
                    },
                  },
                ]
              : selectedReport?.id === 'propertyTaxStatement' || selectedReport?.id === 'ownerStatement'
              ? [
                  {
                    label: 'Download',
                    onClick: () => {
                      selectedOwners.map((owner: $TSFixMe) => {
                        const ownerId = owner.value
                        const ownerName = owner.label
                        downloadReport(
                          selectedReport?.action({
                            start: selectedReport.dateParams.start,
                            finish: selectedReport.dateParams.end,
                            ownerId,
                            ownerName,
                          }),
                        )
                      }),
                        sendDataToGTM({
                          event: 'download_report',
                          report_name: selectedReport?.name,
                        })
                    },
                  },
                ]
              : [
                  {
                    label: 'Download',
                    onClick: () => {
                      downloadReport(
                        selectedReport?.action({
                          ...selectedReport.actionArgs,
                          ...selectedReport.dateParams,
                        }),
                      ),
                        sendDataToGTM({
                          event: 'download_report',
                          report_name: selectedReport?.name,
                        })
                    },
                  },
                ]
          }
        >
          <DimmerLoader loading={isLoading} fadeStyle="flat" bgColor="white">
            <>
              {selectedReport?.dateParams?.start && selectedReport?.dateParams?.end ? (
                <>
                  {!selectedReport?.ownerParams && (
                    <p className={styles['download-intro']}>
                      Select a date range and click the button below to download{' '}
                      {selectedReport.actionArgs.dataType === 'application/pdf' ? 'a PDF' : 'an Excel'} version of this
                      report.
                    </p>
                  )}
                  {selectedReport?.info && selectedReport.info}
                  <DateRangePicker
                    startLabel={selectedReport?.ownerParams ? 'Select a start date' : ''}
                    endLabel={selectedReport?.ownerParams ? 'Select a end date' : ''}
                    startDate={selectedReport.dateParams.start}
                    endDate={selectedReport.dateParams.end}
                    onChange={(date: $TSFixMe, isStartDate: boolean) => {
                      isStartDate
                        ? setSelectedReport({
                            ...selectedReport,
                            dateParams: {
                              ...selectedReport.dateParams,
                              start: format(new Date(date), dateFormat),
                            },
                          })
                        : setSelectedReport({
                            ...selectedReport,
                            dateParams: {
                              ...selectedReport.dateParams,
                              end: format(new Date(date), dateFormat),
                            },
                          })
                    }}
                    inline={false}
                  />
                </>
              ) : (
                <p className={styles['download-intro']}>
                  Click the button below to download an Excel version of this report.
                </p>
              )}
              {selectedReport?.ownerParams && (
                <div className={styles['owner-select']}>
                  <Select
                    isMulti
                    isSearchable
                    name="owners"
                    label="Search Owner(s)"
                    value={selectedOwners}
                    onChange={handleOwnersChange}
                    options={ownersForSelect}
                  />
                </div>
              )}
              {selectedReport?.agentParams && (
                <div className={styles['agent-select']}>
                  <Select
                    isMulti
                    isSearchable
                    name="agents"
                    label="Search Agent(s)"
                    value={selectedAgents}
                    onChange={handleAgentsChange}
                    options={agentsForSelect}
                  />
                </div>
              )}
              {selectedReport?.invoiceTypeParams && (
                <div className={styles['invoice-type-select']}>
                  <Select
                    isMulti
                    isSearchable
                    name="invoiceTypes"
                    label="Search Invoice Type(s)"
                    value={selectedInvoiceTypes}
                    onChange={handleInvoiceTypesChange}
                    options={[selectAllInvoiceTypesOption, ...invoiceTypesForSelect]}
                  />
                </div>
              )}
              {selectedReport?.leaseStatusParams && (
                <div className={styles['lease-statuses-select']}>
                  <Select
                    isMulti
                    isSearchable
                    name="leaseStatuses"
                    label="Select Lease Status(es)"
                    value={selectedLeaseStatuses}
                    onChange={(selected: MultiValue<SelectOption>) => {
                      setSelectedLeaseStatuses(selected)
                    }}
                    options={leaseStatusesForSelect()}
                  />
                </div>
              )}
              {selectedReport?.leaseTypeParams && (
                <div className={styles['lease-type-select']}>
                  <Select
                    isMulti
                    isSearchable
                    name="leaseTypes"
                    label="Select Lease Type(s)"
                    value={selectedLeaseTypes}
                    onChange={(selected: MultiValue<SelectOption>) => {
                      setSelectedLeaseTypes(selected)
                    }}
                    options={leaseTypesForSelect()}
                  />
                </div>
              )}
              {/* {selectedReport?.id === 'tenancySchedule' && (
                <div className={styles['include-drafts']}>
                  <Switch
                    label="Include Draft Leases"
                    checked={isIncludeDraftsSelected}
                    onChange={() => setIsIncludeDraftsSelected(!isIncludeDraftsSelected)}
                  />
                </div>
              )} */}
            </>
          </DimmerLoader>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default ReportingTable
